import * as R from 'ramda'
import { useMemo } from 'react'
import { RoomTypes } from '@weblite/ts-action/room'
import { useRoom, useRooms } from '$logic/room/react-hook'
import { usePvs, usePvSync } from '$logic/pv/react-hook'
import {
  useSubscriptionSync,
  useSubscriptions,
} from '$logic/subscription/react-hook'
import { useWappRoomSync, useWappRooms } from '$logic/wapp-room/react-hook'
import { usePrivateSpace } from '$logic/private-space/react-hook'
import {
  useRoomLastMessageSync,
  useRoomsLastMessage,
} from '$logic/room-last-message/react-hook'
import { getRoomFromRedux } from '$logic/room'
import { calculateUnseenCount } from './service'

export function useRoomUnseenCountAndUnreadMarkSync(roomId: string) {
  const room = useRoom(roomId)
  const pv = usePvSync(roomId)
  const subscription = useSubscriptionSync(roomId)
  const wappRoom = useWappRoomSync(roomId)
  const privateSpace = usePrivateSpace()
  const roomsLastMessage = useRoomLastMessageSync(roomId)

  const data = pv || subscription || wappRoom || privateSpace
  if (!room || !data) return { unseenCount: 0, unreadMark: false }

  const { unreadMark, seen } = data
  const { lastMessage } = roomsLastMessage || {}
  const unseenCount = Math.max(
    0,
    room.type === 'PRIVATE_SPACE'
      ? 0
      : lastMessage?.iid
      ? calculateUnseenCount(lastMessage, seen, room)
      : 0,
  )
  return {
    unseenCount,
    unreadMark,
  }
}

export function useRoomUnseenCountSync(roomId: string) {
  return useRoomUnseenCountAndUnreadMarkSync(roomId).unseenCount
}

// TODO: #optimize
export function useRoomsUnseenCount() {
  const rooms = useRooms()
  const pvs = usePvs()
  const subscriptions = useSubscriptions()
  const wappRooms = useWappRooms()
  const privateSpace = usePrivateSpace()
  const roomsLastMessage = useRoomsLastMessage()

  return R.mapObjIndexed((room, roomId) => {
    const data =
      room.type === 'GROUP' || room.type === 'CHANNEL'
        ? subscriptions[roomId]
        : room.type === 'PV'
        ? pvs[roomId]
        : room.type === 'WAPP_ROOM'
        ? wappRooms[roomId]
        : privateSpace

    if (!data) return { unseenCount: 0, unreadMark: false }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { unreadMark, seen } = data
    const { lastMessage } = roomsLastMessage?.[roomId] || {}
    const unseenCount = Math.max(
      0,
      room.type === 'PRIVATE_SPACE'
        ? 0
        : lastMessage?.iid
        ? calculateUnseenCount(lastMessage, seen, room)
        : 0,
    )
    return {
      unseenCount,
      unreadMark,
    }
  }, rooms)
}

export function useRoomListUnseenCount() {
  const roomsUnseenCount = useRoomsUnseenCount()

  return useMemo(() => {
    const filteredRooms = R.toPairs(roomsUnseenCount)
      .filter(
        ([_, { unseenCount, unreadMark }]) => unseenCount > 0 || unreadMark,
      )
      .map(([roomId, obj]) => [
        roomId,
        R.assoc('type', getRoomFromRedux(roomId).type, obj),
      ]) as [
      string,
      { unseenCount: number; unreadMark: boolean; type: RoomTypes },
    ][]

    const unseenCountByType = R.reduce(
      (acc, item) =>
        R.assoc(
          item[1].type,
          acc[item[1].type] + +(!!item[1].unseenCount || item[1].unreadMark),
          acc,
        ),
      { PV: 0, PRIVATE_SPACE: 0, GROUP: 0, CHANNEL: 0, WAPP_ROOM: 0 },
      filteredRooms,
    )
    return {
      ...unseenCountByType,
      PV: unseenCountByType.PV + unseenCountByType.PRIVATE_SPACE,
      ALL: R.sum(R.values(unseenCountByType)),
    }
  }, [roomsUnseenCount])
}
